import { fetchData, patchData, postData } from 'modules/Axios';
import { showToast } from 'modules/Notifications';
import { resetTable } from 'modules/Table';

export const defaultState = {
  currentPage: 0,
  currentSegment: {
    items: {},
    metaData: {},
    stats: {},
  },
  detailsLoading: false,
  loading: false,
  paginationData: {
    page: 0,
    totalRecords: Number.MAX_SAFE_INTEGER,
  },
  searchValue: '',
  segments: [],
  segmentsFilter: '',
  sortBy: 'modifiedOn',
  sortDirection: 'DESC',
};

export const SEGMENT_UPDATE_SORT = 'SEGMENT_UPDATE_SORT';

export const updateSort = (sortBy, sortDirection) => ({
  type: SEGMENT_UPDATE_SORT,
  payload: {
    sortBy,
    sortDirection,
  }
});

export const updateSortInSegmentsGrid = (sortBy, sortDirection) => {
  return async dispatch => {
    dispatch(updateSort(sortBy, sortDirection));
    dispatch(refetchSegments());
  };
};

export const RECEIVE_SEGMENTS = 'RECEIVE_SEGMENTS';
/**
 * Receive Segments Handler for getSegments
 * @param {Array.Object} segments - Segments payload from API
 * @return {Object} RECEIVE_SEGMENTS Payload
 */
export const receiveSegments = segments => ({
  type: RECEIVE_SEGMENTS,
  payload: segments,
});

export const RESET_SEGMENTS = 'RESET_SEGMENTS';
/**
 * @function resetSegments in Segments Module
 * @description Clears the current list of segments
 * @return {Object} RESET_SEGMENTS action
 */
export const resetSegments = () => ({
  type: RESET_SEGMENTS
});

export const REFETCH_SEGMENTS = 'REFETCH_SEGMENTS';
/**
 * @function refetchSegments
 * @return {Function} Dispatch of setSegmentsTableCacheReset
 */
export const refetchSegments = () => {
  return dispatch => {
    dispatch(resetSegments());
    /* Utilize resetTable method from table module to reset */
    return dispatch(resetTable('segments'));
  };
};

export const RESET_CURRENT_SEGMENT = 'RESET_CURRENT_SEGMENT';
/**
 * Resets currentSegment for DetailsModal
 * @function resetCurrentSegment
 * @return {Object} RESET_CURRENT_SEGMENT Payload
 */
export const resetCurrentSegment = () => ({
  type: RESET_CURRENT_SEGMENT,
});

export const SEGMENTS_LOADING = 'SEGMENT_LOADING';
/**
 * Toggles loading state for Segments Page
 * @function segmentsLoading
 * @param {boolean} bool
 * @return {Object} SEGMENTS_LOADING Payload
 */
export const segmentsLoading = bool => ({
  type: SEGMENTS_LOADING,
  payload: bool,
});

export const SEGMENT_DETAILS_LOADING = 'SEGMENT_DETAILS_LOADING';
/**
 * Toggles loading state of verbose fetch for DetailsModal
 * @function segmentDetailsLoading
 * @param {boolean} bool
 * @return {Object} SEGMENT_DETAILS_LOADING Payload
 */
export const segmentDetailsLoading = bool => ({
  type: SEGMENT_DETAILS_LOADING,
  payload: bool,
});

export const UPDATE_SEGMENTS_PAGE = 'UPDATE_SEGMENTS_PAGE';
/**
 * @function updatePage
 * @description Updates the current page for BE pagination through the reusable Table
 * @param {number} page
 * @return {Object} UPDATE_PAGE Payload
 */
export const updateSegmentsPage = page => ({
  type: UPDATE_SEGMENTS_PAGE,
  payload: page,
});

export const SET_GET_SEGMENTS_FETCHING = 'SET_GET_SEGMENTS_FETCHING';
/**
 * Sets Get Segments Fetching Flag Value
 * @function setGetSegmentsFetching
 * @param {boolean} isLoading
 * @return {Object} SET_GET_SEGMENTS_FETCHING Payload
 */
export const setGetSegmentsFetching = isLoading => ({
  type: SET_GET_SEGMENTS_FETCHING,
  payload: isLoading,
});

export const GET_SEGMENTS = 'GET_SEGMENTS';
/**
 * Fetches List of Segments
 * @function getSegments
 * @param {Number} size - Size of API request
 * @return {Function} Dispatch of `receiveSegments`
 */
export const getSegments = ({ size }) => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const { currentPage, searchValue, segments, segmentsFilter, sortBy, sortDirection } = getState().segments;

    const encodedSearch = encodeURIComponent(searchValue);
    const segmentType = segmentsFilter === 'all' ? '' : segmentsFilter.toUpperCase();
    const showArchived = segmentsFilter === 'archived';
    const URL =
      `/brands/${brand.id}/segments?size=${size}&page=${currentPage}&type=${!showArchived ? segmentType : ''}&name=${encodedSearch}&sort=custom&sort=${sortBy},${sortDirection}${showArchived ? '&archived=true' : '&archived=false'}`;
    const currentGetSegmentsRequest = await dispatch(fetchData(URL))
      .catch(e => {
        /* If token cancellation, is not an actual error return default payload. */
        if (e.message === 'CANCEL_DATA_REQUEST') {
          console.error('API Call to /segments canceled...', e);
          return { segments: [], paginationData: defaultState.paginationData };
        }
        console.error('API Call to /segments failed...', e);
        dispatch(showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue retrieving segments list. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        }));
        return {
          segments: [],
          paginationData: {
            page: 0,
            totalRecords: 0,
          },
        };
      });
    dispatch(updateSegmentsPage(currentPage + 1));
    return dispatch(receiveSegments({
      ...currentGetSegmentsRequest,
      segments: segments.concat(currentGetSegmentsRequest.segments)
    }));
  };
};

export const archiveSegment = id => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;

    await dispatch(postData(`brands/${brand.id}/segments/${id}/archive`, { brand_uuid: brand.id, id }))
      .then(() => {
        dispatch(showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: 'Segment archived successfully',
          dismissible: true,
          dismissAfter: 2000,
        }));
      })
      .catch(e => {
        console.error('API Call to /archive failed...', e);
        dispatch(showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue archiving segment. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        }));
        return null;
      });
  };
};

export const unarchiveSegment = id => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;

    await dispatch(postData(`brands/${brand.id}/segments/${id}/unarchive`, { brand_uuid: brand.id, id }))
      .then(() => {
        dispatch(showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: 'Segment unarchived successfully',
          dismissible: true,
          dismissAfter: 2000,
        }));
      })
      .catch(e => {
        console.error('API Call to /unarchive failed...', e);
        dispatch(showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue unarchiving segment. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        }));
        return null;
      });
  };
};

export const UPDATE_SEGMENT_SEARCH = 'UPDATE_SEGMENTS_SEARCH';
export const updateSegmentsSearch = searchValue => {
  return dispatch => {
    dispatch({ type: UPDATE_SEGMENT_SEARCH, payload: searchValue });
    dispatch(resetSegments());
    /* Reset the Table */
    return dispatch(resetTable('segments'));
  };
};

export const FETCH_SEGMENT_DETAILS = 'FETCH_SEGMENT_DETAILS';
/**
 * Fetches verbose segment data from `/verbose` endpoint
 * @function fetchSegmentDetails
 * @param {Number} id - segmentId
 * @param {Function} closeModalCallback
 * @return {Function} Dispatch of FETCH_SEGMENT_DETAILS Payload
 */
export const fetchSegmentDetails = (id, closeModalCallback) => {
  return async (dispatch, getState) => {
    dispatch(segmentDetailsLoading(true));
    const { brand } = getState().app;

    const segment = await dispatch(fetchData(`/brands/${brand.id}/segments/${id}/verbose`))
      .catch(e => {
        dispatch(showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue retrieving segment details. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        }));
        dispatch(segmentDetailsLoading(false));
        /* Endpoint failed. No data to display -- close the opened modal. */
        closeModalCallback();
        return { ...defaultState.currentSegment };
      });
    return dispatch({
      type: FETCH_SEGMENT_DETAILS,
      payload: segment,
    });
  };
};

/**
 * Activates a "Inactive" segment
 * @function activateSegment
 * @param {Number} id - SegmentId
 * @return {Function} Dispatch of `resetSegments`
 */
export const activateSegment = id => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    await dispatch(patchData(`brands/${brand.id}/segments/${id}/activate`, {}))
      .catch(e => {
        console.error('API Call to /activate failed...', e);
        dispatch(showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue activating segment. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        }));
        return null;
      });
    return dispatch(resetSegments());
  };
};

/**
 * Deactivates an "Active" segment
 * @function deactivateSegment
 * @param {Number} id - SegmentId
 * @return {Function} Dispatch of `resetSegments`
 */
export const deactivateSegment = id => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    await dispatch(patchData(`brands/${brand.id}/segments/${id}/deactivate`, {}))
      .catch(e => {
        console.error('API Call to /deactivate failed...', e);
        dispatch(showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue deactivating segment. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        }));
        return null;
      });
    return dispatch(resetSegments());
  };
};

/**
 * Duplicates a segment
 * @function duplicateSegment
 * @param {Number} id - SegmentId
 * @return {Function} Dispatch of `resetSegments`
 */
export const duplicateSegment = (id, name) => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;

    await dispatch(postData(`brands/${brand.id}/segments/${id}/duplicate`, { name }))
      .catch(e => {
        console.error('API Call to /duplicate failed...', e);
        dispatch(showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue duplicating segment. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        }));
        return null;
      });
    return dispatch(resetSegments());
  };
};

export const RESET_SEGMENTS_ROUTE_DATA = 'RESET_SEGMENTS_ROUTE_DATA';
/**
 * @function resetRouteData in Segments Module
 * @description Resets to Initial State Store Value
 * @return {Function} Dispatch of resetRouteData
 */
export const resetRouteData = () => ({
  type: RESET_SEGMENTS_ROUTE_DATA,
});

export const UPDATE_SEGMENTS_FILTER = 'UPDATE_SEGMENTS_FILTER';
/**
 * @function updateSegmentFilterData in Segments Module
 * @description Updates Filters for Segments Table
 * @param {string} field
 * @param {any} payload
 * @return {Function} Dispatch of getSegments()
 */
export const updateSegmentsFilter = filter => {
  return dispatch => {
    /* If filter updating, disregard any isFetching statuses for getting segments to prep for new fetch with updated filters */
    dispatch(setGetSegmentsFetching(false));
    dispatch({ type: UPDATE_SEGMENTS_FILTER, payload: filter });
    dispatch(resetSegments());
    return dispatch(resetTable('segments'));
  };
};

export default function reducer(state = defaultState, action = {}) {
  switch (action.type) {
    case FETCH_SEGMENT_DETAILS:
      return {
        ...state,
        currentSegment: action.payload,
        detailsLoading: false,
      };
    case RECEIVE_SEGMENTS:
      return {
        ...state,
        segments: action.payload.segments,
        paginationData: action.payload.paginationData,
      };
    case RESET_CURRENT_SEGMENT:
      return { ...state, currentSegment: {} };
    case RESET_SEGMENTS_ROUTE_DATA:
      return { ...defaultState };
    case RESET_SEGMENTS:
      return {
        ...state,
        currentPage: 0,
        paginationData: { ...defaultState.paginationData },
        segments: [],
      };
    case SEGMENT_DETAILS_LOADING:
      return { ...state, detailsLoading: action.payload };
    case SEGMENTS_LOADING:
      return { ...state, loading: action.payload };
    case UPDATE_SEGMENTS_FILTER:
      return { ...state, segmentsFilter: action.payload };
    case UPDATE_SEGMENTS_PAGE:
      return { ...state, currentPage: action.payload };
    case UPDATE_SEGMENT_SEARCH:
      return { ...state, searchValue: action.payload };
    case SEGMENT_UPDATE_SORT:
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortDirection: action.payload.sortDirection,
      };
    default:
      return state;
  }
}
